import { range } from '@core/utils/remeda';
import { Skeleton, Table as ChakraTable } from '@chakra-ui/react';
import {
  flexRender,
  useReactTable,
  getCoreRowModel,
  type RowData,
  type ColumnDef,
} from '@tanstack/react-table';

import type { JSX } from 'react';

interface ITableProps<T extends RowData> {
  data: T[];
  isLoading?: boolean;
  columns: ColumnDef<T>[];
}

export function Table<T extends RowData>({
  data,
  columns,
  isLoading = false,
}: ITableProps<T>): JSX.Element {
  const { getHeaderGroups, getRowModel } = useReactTable({
    columns,
    getCoreRowModel: getCoreRowModel(),
    data: isLoading ? range(0, 10).map<T>(() => ({}) as T) : data,
  });

  const { rows } = getRowModel();

  return (
    <ChakraTable.Root>
      <ChakraTable.Header>
        {getHeaderGroups().map(({ id, headers }) => {
          return (
            <ChakraTable.Row key={id}>
              {headers.map(({ id, colSpan, column, getContext }) => {
                return (
                  <ChakraTable.ColumnHeader
                    key={id}
                    textAlign='center'
                    colSpan={colSpan}
                  >
                    {flexRender(column.columnDef.header, getContext())}
                  </ChakraTable.ColumnHeader>
                );
              })}
            </ChakraTable.Row>
          );
        })}
      </ChakraTable.Header>
      <ChakraTable.Body>
        {rows.map(({ id, getVisibleCells }) => {
          return (
            <ChakraTable.Row key={id}>
              {getVisibleCells().map(({ id, column, getContext }) => {
                return (
                  <ChakraTable.Cell textAlign='center' px={2.5} py={3} key={id}>
                    {isLoading ? (
                      <Skeleton height='20px' />
                    ) : (
                      flexRender(column.columnDef.cell, getContext())
                    )}
                  </ChakraTable.Cell>
                );
              })}
            </ChakraTable.Row>
          );
        })}
        {!rows.length && (
          <ChakraTable.Row>
            <ChakraTable.Cell
              py={4}
              border={0}
              textAlign='center'
              colSpan={columns.length}
            >
              No record at the moment.
            </ChakraTable.Cell>
          </ChakraTable.Row>
        )}
      </ChakraTable.Body>
    </ChakraTable.Root>
  );
}
